import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from 'react';
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { blockPropertyDatesURL, getBookingPropertyURL } from "../../../utils/urls";
import BookingCalendar from "./booking_calendar";

const BlockDatesComponent = ({
  visible,
  onHide,
  facilityId,
  propertyData,
  blockData = null, // Pass for editing an existing block
  existingBlocks = [], // All existing blocked dates for this property
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    reason: "Renovation", // Changed default from "Reservation" to "Renovation"
    notes: ""
  });
  const [mode, setMode] = useState('add'); // 'add', 'edit', or 'view'
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Options for blocking reasons
  const blockingReasons = [
    { label: "Renovation", value: "Renovation" },
    { label: "Owner Use", value: "Owner Use" }
  ];

  const normalizeReason = (reason) => {
    if (reason === "Owner Use") return "Owner Use";
    return "Renovation";
  };

  // CRITICAL FIX: Ensure dates are correctly normalized to prevent month shift
  const normalizeDate = (date) => {
    if (!date) return null;

    // Create a fresh date object 
    const newDate = new Date(date);

    // Log the original date for debugging
    console.log('Original date:', newDate.toString());
    console.log('Original date components:', {
      year: newDate.getFullYear(),
      month: newDate.getMonth(),
      day: newDate.getDate()
    });

    // Extract year, month, and day directly
    const year = newDate.getFullYear();
    const month = newDate.getMonth(); // Keep month as-is (0-11)
    const day = newDate.getDate();

    // Create a new date using only these components with time set to midnight
    const normalizedDate = new Date(year, month, day, 0, 0, 0, 0);

    // Verify normalization worked correctly
    console.log('Normalized date:', normalizedDate.toString());
    console.log('Normalized date components:', {
      year: normalizedDate.getFullYear(),
      month: normalizedDate.getMonth(),
      day: normalizedDate.getDate()
    });

    return normalizedDate;
  };

  // Initialize component
  useEffect(() => {
    // Reset the flag when dialog visibility changes
    if (!visible) {
      setInitialDataLoaded(false);
      return;
    }

    // If this is the first time the dialog is shown, initialize it
    if (!initialDataLoaded) {
      initializeComponent();
      setInitialDataLoaded(true);
    }
  }, [visible]);

  const initializeComponent = () => {
    // If we have blockData (for editing), set mode to edit
    if (blockData) {
      const isReservationBlock = blockData.reason === 'Reservation';
      setMode(isReservationBlock ? 'view' : 'edit');
      setSelectedBlock(blockData);

      // Properly normalize dates when editing
      const startDate = blockData.startDate ? normalizeDate(new Date(blockData.startDate)) : null;
      const endDate = blockData.endDate ? normalizeDate(new Date(blockData.endDate)) : null;

      setFormData({
        startDate,
        endDate,
        reason: blockData.reason || "Renovation",
        notes: blockData.notes || ""
      });
    } else {
      // Otherwise, start in add mode
      setMode('add');
      setFormData({
        startDate: null,
        endDate: null,
        reason: "Renovation",
        notes: ""
      });
      setSelectedBlock(null);
    }

    // Load existing blocks
    if (Array.isArray(existingBlocks) && existingBlocks.length > 0) {
      setBlockedDates(existingBlocks.map(block => ({
        ...block,
        startDate: normalizeDate(new Date(block.startDate)),
        endDate: normalizeDate(new Date(block.endDate))
      })));
    } else {
      // Try to fetch existing blocks if not provided
      if (propertyData && (propertyData.id || propertyData._id)) {
        // Check if the property already has blocked dates in it
        if (propertyData.blockedDates && Array.isArray(propertyData.blockedDates)) {
          const formattedBlocks = propertyData.blockedDates.map(block => ({
            ...block,
            id: block._id || block.id,
            startDate: normalizeDate(new Date(block.startDate)),
            endDate: normalizeDate(new Date(block.endDate))
          }));
          setBlockedDates(formattedBlocks);
        } else {
          // Only fetch from API if property doesn't already have blockedDates
          fetchBookingProperty();
        }
      }
    }
  };

  // Fetch booking property to get blocked dates
  const fetchBookingProperty = async () => {
    try {
      setLoading(true);

      // Get the property ID correctly
      const propertyId = propertyData?._id || // If propertyData is the apartment itself
        propertyData?.id || // Alternative ID format
        propertyData?.unit?._id || // If propertyData has a apartment reference
        propertyData?.unit?.id; // Alternative ID format for apartment reference

      console.log("Property data:", propertyData);
      console.log("Using property ID:", propertyId);

      if (!propertyId) {
        const errorMsg = "Property ID is missing";
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      const response = await makeRequest2(
        `${getBookingPropertyURL}/${facilityId}/${propertyId}`,
        "GET"
      );

      if (response.success) {
        const property = response.data?.data || response.data;

        if (property && property.blockedDates && Array.isArray(property.blockedDates)) {
          const formattedBlocks = property.blockedDates.map(block => ({
            ...block,
            id: block._id || block.id,
            startDate: normalizeDate(new Date(block.startDate)),
            endDate: normalizeDate(new Date(block.endDate))
          }));
          setBlockedDates(formattedBlocks);
        } else {
          console.log("No blocked dates found for this property");
          setBlockedDates([]);
        }
      } else {
        console.error("Failed to fetch property details:", response.error);
        setBlockedDates([]);
      }
    } catch (error) {
      console.error("Error fetching property details:", error);
      setBlockedDates([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString();
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Submit started with propertyData:", propertyData);

      if (!propertyData) {
        const errorMsg = "No apartment/property data available";
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      if (!formData.startDate || !formData.endDate) {
        const errorMsg = "Start date and end date are required";
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      // Make sure dates are normalized
      const normalizedStartDate = normalizeDate(formData.startDate);
      const normalizedEndDate = normalizeDate(formData.endDate);

      // Log the normalized dates to verify
      console.log("Normalized dates for API submission:", {
        startDate: normalizedStartDate.toString(),
        startMonth: normalizedStartDate.getMonth(),
        endDate: normalizedEndDate.toString(),
        endMonth: normalizedEndDate.getMonth()
      });

      // Validate that end date is after start date
      if (normalizedEndDate < normalizedStartDate) {
        const errorMsg = "End date must be after start date";
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      // Validate reason is selected
      if (!formData.reason) {
        const errorMsg = "Please select a reason for blocking";
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      // Get property ID correctly
      const propertyId = propertyData?._id ||
        propertyData?.id ||
        propertyData?.unit?._id ||
        propertyData?.unit?.id;

      console.log("Property data for submission:", propertyData);
      console.log("Using property ID for submission:", propertyId);

      if (!propertyId) {
        const errorMsg = "Property ID is missing";
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      // CRITICAL FIX: Use normalized dates in payload
      // Make sure to use toISOString only when sending to API
      const payload = {
        startDate: normalizedStartDate.toISOString(),
        endDate: normalizedEndDate.toISOString(),
        reason: normalizeReason(formData.reason),
        notes: formData.notes
      };

      console.log("Block dates payload:", payload);

      let response;

      if (mode === 'edit' && selectedBlock && selectedBlock.id) {
        // Update existing block
        response = await makeRequest2(
          `${blockPropertyDatesURL}/${facilityId}/${propertyId}/blocks/${selectedBlock.id}`,
          "PUT",
          payload
        );
      } else {
        // Create new block
        response = await makeRequest2(
          `${blockPropertyDatesURL}/${facilityId}/${propertyId}`,
          "POST",
          payload
        );
      }

      if (response.success) {
        const actionText = mode === 'edit' ? "updated" : "blocked";
        toastify(`Apartment dates ${actionText} successfully`, "success");

        // Call onSuccess callback
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(response.data);
        }

        // Close dialog or reset form depending on mode
        if (mode === 'edit') {
          setMode('add');
          resetForm();
          // Update local blocked dates list
          if (response.data?.blockedDates && Array.isArray(response.data.blockedDates)) {
            const formattedBlocks = response.data.blockedDates.map(block => ({
              ...block,
              id: block._id || block.id,
              startDate: normalizeDate(new Date(block.startDate)),
              endDate: normalizeDate(new Date(block.endDate))
            }));
            setBlockedDates(formattedBlocks);
          }
        } else {
          onHide();
        }
      } else {
        throw new Error(response.error || `Failed to ${mode === 'edit' ? 'update' : 'block'} apartment dates`);
      }
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'blocking'} apartment dates:`, error);
      setError(error.message || `Failed to ${mode === 'edit' ? 'update' : 'block'} apartment dates`);
      toastify(`Failed to ${mode === 'edit' ? 'update' : 'block'} apartment dates`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle unblocking dates
  const handleUnblock = (block) => {
    confirmDialog({
      message: 'Are you sure you want to unblock these dates?',
      header: 'Confirm Unblock',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => unblockDates(block)
    });
  };

  // Unblock the dates
  const unblockDates = async (block) => {
    try {
      setLoading(true);

      // Get the property ID safely
      const propertyId = propertyData?._id ||
        propertyData?.id ||
        propertyData?.unit?._id ||
        propertyData?.unit?.id;

      if (!propertyId) {
        const errorMsg = "Property ID is missing";
        setError(errorMsg);
        toastify(errorMsg, "error");
        return;
      }

      // Use this propertyId for the API call
      const response = await makeRequest2(
        `${blockPropertyDatesURL}/${facilityId}/${propertyId}/blocks/${block.id}`,
        "DELETE"
      );

      if (response.success) {
        toastify("Dates unblocked successfully", "success");

        // Update the local state by removing the deleted block
        setBlockedDates(prevBlocks => prevBlocks.filter(b => b.id !== block.id));

        // Call onSuccess callback
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(null);
        }
      } else {
        throw new Error(response.error || "Failed to unblock dates");
      }
    } catch (error) {
      console.error("Error unblocking dates:", error);
      toastify("Failed to unblock dates", "error");
    } finally {
      setLoading(false);
    }
  };

  // Reset form and go back to add mode
  const resetForm = () => {
    setFormData({
      startDate: null,
      endDate: null,
      reason: "Renovation",
      notes: ""
    });
    setSelectedBlock(null);
    setMode('add');
  };

  // Edit an existing block
  const editBlock = (block) => {
    if (block.reason === 'Reservation') {
      setMode('view');
      setSelectedBlock(block);
      return;
    }

    setSelectedBlock(block);
    // Properly normalize dates when editing an existing block
    const startDate = normalizeDate(new Date(block.startDate));
    const endDate = normalizeDate(new Date(block.endDate));

    setFormData({
      startDate,
      endDate,
      reason: block.reason || "Renovation",
      notes: block.notes || ""
    });
    setMode('edit');
  };

  // Action column template
  const actionTemplate = (rowData) => {
    if (rowData.reason === 'Reservation') {
      return (
        <span className="text-muted small">Reservation block</span>
      );
    }

    return (
      <div className="flex gap-2 justify-content-center">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => editBlock(rowData)}
          tooltip="Edit"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleUnblock(rowData)}
          tooltip="Unblock"
        />
      </div>
    );
  };

  // Footer buttons for the dialog
  const renderFooter = () => (
    <div className="d-flex justify-content-end gap-2">
      {mode === 'edit' && (
        <Button
          label="Cancel Edit"
          icon="pi pi-times"
          className="p-button-text"
          onClick={resetForm}
          disabled={loading}
        />
      )}
      <Button
        label="Close"
        icon="pi pi-times"
        className="p-button-text"
        onClick={onHide}
        disabled={loading}
      />
      <Button
        label={mode === 'edit' ? "Save Changes" : "Block Dates"}
        icon={mode === 'edit' ? "pi pi-check" : "pi pi-calendar-minus"}
        className="p-button-primary p-button-raised"
        onClick={handleSubmit}
        loading={loading}
        disabled={mode === 'view'}
      />
    </div>
  );

  // Get the property ID to use for the calendar
  const propertyId = propertyData?.id || propertyData?._id || propertyData?.unit?.id;

  return (
    <>
      <ConfirmDialog />
      <Dialog
        header={mode === 'edit' ? "Edit Blocked Dates" : "Block Apartment Dates"}
        visible={visible}
        style={{ width: '650px', borderRadius: '12px', overflow: 'hidden' }}
        onHide={onHide}
        footer={renderFooter()}
        draggable={false}
        resizable={false}
        blockScroll
      >
        {error && (
          <div className="alert alert-danger mb-3">
            <p className="mb-0">{error}</p>
          </div>
        )}

        <div className="p-fluid mb-4">
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label">Select Dates to Block <span className="text-danger">*</span>
              </label>
              <BookingCalendar
                bookingPropertyId={propertyId}
                selectedDates={[
                  formData.startDate instanceof Date ? formData.startDate : null,
                  formData.endDate instanceof Date ? formData.endDate : null
                ].filter(Boolean)}
                onDateChange={(e) => {
                  if (mode === 'view') return;
                  console.log("Date selection changed:", e);

                  // Handle empty selection or clear button
                  if (!e.value || e.value.length === 0) {
                    setFormData(prev => ({
                      ...prev,
                      startDate: null,
                      endDate: null
                    }));
                    return;
                  }

                  // Handle date selection based on array contents
                  if (Array.isArray(e.value)) {
                    if (e.value.length === 1) {
                      // Only start date selected
                      const normalizedDate = normalizeDate(e.value[0]);
                      setFormData(prev => ({
                        ...prev,
                        startDate: normalizedDate,
                        endDate: null
                      }));
                    } else if (e.value.length === 2) {
                      // Both dates selected - make sure they're normalized
                      const normalizedStartDate = normalizeDate(e.value[0]);
                      const normalizedEndDate = normalizeDate(e.value[1]);

                      setFormData(prev => ({
                        ...prev,
                        startDate: normalizedStartDate,
                        endDate: normalizedEndDate
                      }));
                    }
                  } else if (e.value) {
                    // Single value (not array)
                    const normalizedDate = normalizeDate(e.value);
                    setFormData(prev => ({
                      ...prev,
                      startDate: normalizedDate,
                      endDate: null
                    }));
                  }
                }}
                minDate={new Date()}
                showBlockedReason={true}
              />
              <small className="text-muted d-block mt-2">Already blocked dates are shown in gray</small>
            </div>

            {/* Reason dropdown */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Reason <span className="text-danger">*</span></label>
              <Dropdown
                value={formData.reason}
                options={blockingReasons}
                onChange={(e) => handleFormChange('reason', e.value)}
                placeholder="Select a reason"
                className="w-100"
                optionLabel="label"
                optionValue="value"
                disabled={mode === 'view'}
              />
            </div>

            {/* Notes field */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Notes</label>
              <InputText
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="Optional notes"
                className="w-100"
                disabled={mode === 'view'}
              />
            </div>
          </div>
        </div>

        {/* Display existing blocks */}
        {blockedDates.length > 0 && (
          <div>
            <h5 className="mb-3">Currently Blocked Dates</h5>
            <DataTable
              value={blockedDates}
              paginator
              rows={5}
              rowsPerPageOptions={[5, 10]}
              size="small"
              emptyMessage="No blocked dates found"
              className="mb-3"
            >
              <Column field="reason" header="Reason" sortable />
              <Column
                header="Start Date"
                sortable
                body={(rowData) => formatDate(rowData.startDate)}
              />
              <Column
                header="End Date"
                sortable
                body={(rowData) => formatDate(rowData.endDate)}
              />
              <Column
                header="Actions"
                body={actionTemplate}
                style={{ width: '100px', textAlign: 'center' }}
              />
            </DataTable>
          </div>
        )}

        <style jsx="true">{`
          .selected-date {
            background-color: var(--primary-color);
            color: white;
            border-radius: 50%;
            padding: 2px 6px;
          }
          
          .blocked-date {
            background-color: var(--red-500);
            color: white;
            border-radius: 50%;
            padding: 2px 6px;
          }
          
          .date-range-band {
            border-left: 3px solid var(--primary-color);
          }
        `}</style>
      </Dialog>
    </>
  );
};

export default BlockDatesComponent;
